<template>
  <PartnerCheckoutError v-if="error" />

  <div
    v-else
    class="flex min-h-screen grow flex-col items-center justify-center gap-16"
  >
    <div class="flex flex-col items-center gap-8 text-center">
      <p class="body-1 flex items-center gap-8">
        {{ i18n(translations.loadingMessage) }}
        <PartnerLogo :partner-name />
      </p>
      <p class="body-2 text-static-default-low">
        {{ i18n(translations.loadingSubMessage) }}
      </p>
    </div>
    <RevSpinner />
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { watch } from 'vue'

import PartnerLogo from '@backmarket/nuxt-layer-checkout/components/PartnerLogo/PartnerLogo.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevSpinner } from '@ds/components/Spinner'

import { CHECKOUT_E_COMMERCE } from '~/scopes/checkout/flows/e-commerce/route-names'

import PartnerCheckoutError from './PartnerCheckoutError.vue'
import translations from './PartnerCheckoutPage.translations'

const i18n = useI18n()
const route = useRoute()
const $httpFetch = useHttpFetch()
const logger = useLogger()
const { setContext } = useTracking()

const partnerName = String(route.params.partnerName)

const { error } = await useAsyncData(
  'partner-checkout',
  async () => {
    const listingId = String(route.query.listingId)

    setContext({ utm_source: partnerName })

    const cartOps = async () => {
      await $httpFetch('/bm/cart', { method: 'DELETE' })
      await $httpFetch('/bm/cart/add_product', {
        method: 'POST',
        body: { listing_id: listingId },
      })
    }

    // Run cart API calls and a minimum display delay in parallel so the loader
    // stays visible for at least 3 seconds, giving users time to read the page.
    await Promise.all([
      cartOps(),
      new Promise<void>((resolve) => {
        setTimeout(resolve, 3000)
      }),
    ])

    await navigateTo({
      name: CHECKOUT_E_COMMERCE.CART,
      query: { partner: partnerName },
    })
  },
  { server: false },
)

watch(error, (value) => {
  if (value) {
    logger.error('Partner checkout failed', {
      partnerName,
      error: value,
      owners: ['bot-squad-partnerships-front'],
    })
  }
})
</script>
